import cn from 'classnames';
import React, { memo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import Image from 'components/Image';
import { Col, Row } from 'components/layout';
import { InputV2 } from 'components/ComponentV2';
import { REGEX } from 'lib/utils/regex';

import Gamification, { GamificationProps } from '../Gamification';
import SectionWrapper from '../SectionWrapper';

import styles from './SocialLinks.module.scss';

export interface SocialLinksProps extends GamificationProps {
  showProgressBanner: boolean;
  sectionClass?: string;
}

export const SocialLinks: React.FC<SocialLinksProps> = memo(
  ({
    showProgressBanner,
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
    sectionClass,
  }: SocialLinksProps) => {
    const contentClassNames = cn({
      [styles.SocietyProfileDetailsForm]: true,
    });

    const [showProgressBannerState, setShowProgressBannerState] =
      React.useState<boolean>(showProgressBanner);

    useEffect(() => {
      if (showProgressBannerState === false)
        setShowProgressBannerState(showProgressBanner);
    }, [showProgressBanner]);

    const {
      register,
      formState: { errors },
    } = useFormContext();

    return (
      <div id={`section_${progressDetails?.id}`} className={sectionClass}>
        {showProgressBannerState && (
          <Gamification
            profileCompletePercent={profileCompletePercent}
            nonCompletedSection={nonCompletedSection}
            progressDetails={progressDetails}
            handleProfileCompletionButton={() => {
              setShowProgressBannerState(false);
              handleProfileCompletionButton();
            }}
            handleNextButton={() => {
              setShowProgressBannerState(false);
              handleNextButton && handleNextButton();
            }}
            showNextButton={showNextButton}
          />
        )}

        <SectionWrapper
          title="Social media links"
          description="Add your social media links so candidates can explore and learn more about your company!"
          helperText="(Optional)"
          className={cn({ [styles.noBorderRadius]: showProgressBannerState })}
        >
          <div className={contentClassNames}>
            <Row className={styles.SocietyProfileDetailsFormRow} gutter={50}>
              <Col>
                <div
                  className={
                    styles.SocietyProfileDetailsFormSocialLinkContainer
                  }
                >
                  <Row
                    className={
                      styles.SocietyProfileDetailsFormSocialLinkContainerRow
                    }
                  >
                    <Col
                      span={24}
                      className={styles.SocietyProfileDetailsFormSocialLink}
                    >
                      <Image src="/images/linkedin.png" alt="linkedin" />
                      <InputV2
                        placeholder="Add your LinkedIn link"
                        className={
                          styles.SocietyProfileDetailsFormSocialLinkInput
                        }
                        {...register('linkedin_url', {
                          pattern: {
                            value: REGEX.URL,
                            message: 'Enter a valid url',
                          },
                        })}
                        isInvalid={!!errors.linkedin_url}
                        helperText={errors.linkedin_url?.message}
                      />
                    </Col>
                    <Col
                      span={24}
                      className={styles.SocietyProfileDetailsFormSocialLink}
                    >
                      <Image src="/images/instagram.png" alt="instagram" />
                      <InputV2
                        placeholder="Add your Instagram link"
                        className={
                          styles.SocietyProfileDetailsFormSocialLinkInput
                        }
                        {...register('instagram_url', {
                          pattern: {
                            value: REGEX.URL,
                            message: 'Enter a valid url',
                          },
                        })}
                        isInvalid={!!errors.instagram_url}
                        helperText={errors.instagram_url?.message}
                      />
                    </Col>
                    <Col
                      span={24}
                      className={styles.SocietyProfileDetailsFormSocialLink}
                    >
                      <Image src="/images/facebook.png" alt="facebook" />
                      <InputV2
                        placeholder="Add your Facebook link"
                        className={
                          styles.SocietyProfileDetailsFormSocialLinkInput
                        }
                        {...register('facebook_url', {
                          pattern: {
                            value: REGEX.URL,
                            message: 'Enter a valid url',
                          },
                        })}
                        isInvalid={!!errors.facebook_url}
                        helperText={errors.facebook_url?.message}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>
        </SectionWrapper>
      </div>
    );
  }
);

SocialLinks.displayName = 'SocialLinks';
